import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';

export interface EyeLogoRef {
  squint: () => void;
  successSequence: (onComplete: () => void) => Promise<void>;
  reset: () => void;
}

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeOut = (t: number) => 1 - Math.pow(1 - clamp(t, 0, 1), 3);
const easeOutBack = (t: number) => {
  if (t >= 1) return 1;
  const c1 = 1.4;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

function runAnimation(dur: number, fn: (t: number) => void, cb?: () => void) {
  const t0 = performance.now();
  let frameId = 0;
  
  function frame(n: number) {
    const t = clamp((n - t0) / dur, 0, 1);
    fn(t);
    if (t < 1) {
      frameId = requestAnimationFrame(frame);
    } else if (cb) {
      cb();
    }
  }
  
  frameId = requestAnimationFrame(frame);
  return () => cancelAnimationFrame(frameId);
}

interface EyeLogoProps {
  width?: string | number;
  height?: string | number;
}

export const EyeLogo = forwardRef<EyeLogoRef, EyeLogoProps>(({ width = "260", height = "160" }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const irisOffsetRef = useRef<SVGGElement>(null);
  const irisScaleRef = useRef<SVGGElement>(null);
  const irisCircleRef = useRef<SVGCircleElement>(null);
  
  const pr1Ref = useRef<SVGCircleElement>(null);
  const pr2Ref = useRef<SVGCircleElement>(null);
  const pr3Ref = useRef<SVGCircleElement>(null);

  // Animation states and targets
  const trackingActive = useRef(true);
  const currentX = useRef(0);
  const currentY = useRef(0);
  const targetX = useRef(0);
  const targetY = useRef(0);
  const squinting = useRef(false);

  // Mouse move handler
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!trackingActive.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const eyeCenterX = rect.left + rect.width / 2;
      const eyeCenterY = rect.top + rect.height / 2;
      const dx = e.clientX - eyeCenterX;
      const dy = e.clientY - eyeCenterY;
      const dist = Math.hypot(dx, dy) || 1;
      const maxDim = Math.max(window.innerWidth, window.innerHeight) * 0.5;
      const factor = Math.min(dist / maxDim, 1);
      
      targetX.current = (dx / dist) * 20 * factor;
      targetY.current = (dy / dist) * 20 * factor * 0.6;
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Frame loop for smooth iris tracking
  useEffect(() => {
    let frameId = 0;
    
    function updateIris() {
      if (trackingActive.current) {
        currentX.current += (targetX.current - currentX.current) * 0.08;
        currentY.current += (targetY.current - currentY.current) * 0.08;
        if (irisOffsetRef.current) {
          irisOffsetRef.current.setAttribute(
            'transform',
            `translate(${currentX.current.toFixed(2)},${currentY.current.toFixed(2)})`
          );
        }
      }
      frameId = requestAnimationFrame(updateIris);
    }
    
    updateIris();
    return () => cancelAnimationFrame(frameId);
  }, []);

  const setIrisScale = (s: number) => {
    if (irisScaleRef.current) {
      irisScaleRef.current.setAttribute(
        'transform',
        `translate(110,80) scale(${s.toFixed(4)}) translate(-110,-80)`
      );
    }
  };

  const fireRing = (el: SVGCircleElement | null, r0: number, r1: number, dur: number) => {
    if (!el) return;
    runAnimation(dur, (t) => {
      const e = easeOut(t);
      el.setAttribute('r', lerp(r0, r1, e).toFixed(1));
      el.setAttribute('stroke', `rgba(0,255,209,${(0.65 * (1 - e)).toFixed(3)})`);
    }, () => {
      el.setAttribute('stroke', 'rgba(0,255,209,0)');
      el.setAttribute('r', '34');
    });
  };

  // Expose imperative functions to parents
  useImperativeHandle(ref, () => ({
    squint: () => {},

    successSequence: async (onComplete: () => void) => {
      trackingActive.current = false;
      const iris = irisCircleRef.current;
      if (!iris) return;

      // 1. Center the iris offset
      await new Promise<void>((resolve) => {
        runAnimation(350, (t) => {
          const e = easeOut(t);
          const valX = currentX.current * (1 - e);
          const valY = currentY.current * (1 - e);
          if (irisOffsetRef.current) {
            irisOffsetRef.current.setAttribute('transform', `translate(${valX.toFixed(2)},${valY.toFixed(2)})`);
          }
        }, resolve);
      });

      // 2. Glow up iris, expand iris scale, and fire rings
      iris.setAttribute('filter', 'url(#irisGlowBig)');
      iris.setAttribute('fill', 'url(#irisHot)');
      runAnimation(700, (t) => {
        setIrisScale(1 + easeOut(t) * 0.22);
      });

      setTimeout(() => fireRing(pr1Ref.current, 30, 70, 600), 0);
      setTimeout(() => fireRing(pr2Ref.current, 30, 90, 650), 130);
      setTimeout(() => fireRing(pr3Ref.current, 30, 115, 700), 260);

      await new Promise((r) => setTimeout(r, 800));

      // 3. Heartbeat beats
      const beat = (delta: number, dur: number) => {
        return new Promise<void>((res) => {
          runAnimation(dur * 0.45, (t) => setIrisScale(lerp(1.22, 1.22 + delta, easeOut(t))), () => {
            runAnimation(dur * 0.55, (t) => setIrisScale(lerp(1.22 + delta, 1.22, easeOut(t))), res);
          });
        });
      };

      await beat(0.16, 210);
      await new Promise((r) => setTimeout(r, 65));
      await beat(0.11, 180);
      await new Promise((r) => setTimeout(r, 55));
      await beat(0.08, 150);
      await new Promise((r) => setTimeout(r, 400));

      // 4. Complete
      onComplete();
    },

    reset: () => {
      trackingActive.current = true;
      squinting.current = false;
      const iris = irisCircleRef.current;
      if (iris) {
        iris.setAttribute('fill', 'url(#irisNorm)');
        iris.setAttribute('filter', 'url(#irisGlow)');
      }
      setIrisScale(1);
      if (irisOffsetRef.current) {
        irisOffsetRef.current.setAttribute('transform', 'translate(0,0)');
      }
      targetX.current = 0;
      targetY.current = 0;
      currentX.current = 0;
      currentY.current = 0;
    }
  }));

  return (
    <div className="eye-wrap select-none pointer-events-none" id="eyeWrap" ref={containerRef}>
      {/* Background glow behind eye */}
      <div className="eye-bg-glow absolute inset-[-30px] rounded-full bg-[radial-gradient(circle,rgba(0,255,209,0.18)_0%,transparent_65%)] animate-[glowIn_2.4s_ease-out_0.4s_forwards,glowPulse_3s_ease-in-out_2.8s_infinite]" />

      <svg
        id="eyeSvg"
        width={width}
        height={height}
        viewBox="0 0 220 160"
        xmlns="http://www.w3.org/2000/svg"
        className="overflow-hidden relative z-[2] opacity-0 animate-[eyeReveal_1.8s_ease-out_0.2s_forwards]"
      >
        <defs>
          <clipPath id="eyeClip">
            <path d="M30,80 Q70,44 110,42 Q150,44 190,80 Q150,116 110,118 Q70,116 30,80 Z" />
          </clipPath>
          <radialGradient id="irisNorm" cx="50%" cy="40%" r="50%">
            <stop offset="0%" stopColor="#00ffd1" stopOpacity="0.95" />
            <stop offset="40%" stopColor="#7c3aed" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#1a0a2e" />
          </radialGradient>
          <radialGradient id="irisHot" cx="50%" cy="40%" r="50%">
            <stop offset="0%" stopColor="#00ffd1" />
            <stop offset="30%" stopColor="#00c8a8" />
            <stop offset="100%" stopColor="#001a12" />
          </radialGradient>
          <radialGradient id="pupilGrad" cx="50%" cy="35%" r="50%">
            <stop offset="0%" stopColor="#001a14" />
            <stop offset="100%" stopColor="#000" />
          </radialGradient>
          <radialGradient id="sheenGrad" cx="30%" cy="30%" r="60%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
          <filter id="irisGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="irisGlowBig" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="8" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="b" />
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="lashGlow" x="-10%" y="-30%" width="120%" height="160%">
            <feGaussianBlur stdDeviation="2.5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="ringGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* sclera */}
        <path
          d="M30,80 Q70,44 110,42 Q150,44 190,80 Q150,116 110,118 Q70,116 30,80 Z"
          fill="#d8f0ec"
          clipPath="url(#eyeClip)"
        />

        {/* iris: clipped to eye, scales from (110,80) via irisScale group */}
        <g id="irisScale" ref={irisScaleRef} clipPath="url(#eyeClip)">
          <g id="irisOffset" ref={irisOffsetRef}>
            <circle
              id="irisCircle"
              ref={irisCircleRef}
              cx="110"
              cy="80"
              r="27"
              fill="url(#irisNorm)"
              filter="url(#irisGlow)"
            />
            <circle cx="110" cy="80" r="23" fill="none" stroke="rgba(0,255,209,0.25)" strokeWidth="1" />
            <circle cx="110" cy="80" r="18" fill="none" stroke="rgba(0,255,209,0.15)" strokeWidth="0.75" />
            <g stroke="rgba(0,200,168,0.22)" strokeWidth="0.75">
              <line x1="110" y1="55" x2="110" y2="62" />
              <line x1="110" y1="98" x2="110" y2="105" />
              <line x1="85" y1="80" x2="92" y2="80" />
              <line x1="128" y1="80" x2="135" y2="80" />
              <line x1="92" y1="62" x2="96" y2="66" />
              <line x1="128" y1="62" x2="124" y2="66" />
              <line x1="92" y1="98" x2="96" y2="94" />
              <line x1="128" y1="98" x2="124" y2="94" />
            </g>
            <circle cx="110" cy="80" r="14" fill="url(#pupilGrad)" />
            <ellipse cx="103" cy="72" rx="5.5" ry="4.5" fill="url(#sheenGrad)" opacity="0.85" />
            <ellipse cx="119" cy="76" rx="2" ry="1.5" fill="rgba(255,255,255,0.45)" />
          </g>
        </g>

        {/* pulse rings inside SVG — always centered on iris at (110,80) */}
        <circle ref={pr1Ref} id="pr1" cx="110" cy="80" r="34" fill="none" stroke="rgba(0,255,209,0)" strokeWidth="1.5" filter="url(#ringGlow)" />
        <circle ref={pr2Ref} id="pr2" cx="110" cy="80" r="34" fill="none" stroke="rgba(0,255,209,0)" strokeWidth="1.5" filter="url(#ringGlow)" />
        <circle ref={pr3Ref} id="pr3" cx="110" cy="80" r="34" fill="none" stroke="rgba(0,255,209,0)" strokeWidth="1.5" filter="url(#ringGlow)" />

        {/* lashes always on top, not scaled */}
        <path
          d="M30,80 Q70,44 110,42 Q150,44 190,80"
          fill="none"
          stroke="rgba(255,255,255,0.9)"
          strokeWidth="2.5"
          strokeLinecap="round"
          filter="url(#lashGlow)"
        />
        <path
          d="M30,80 Q70,116 110,118 Q150,116 190,80"
          fill="none"
          stroke="rgba(255,255,255,0.55)"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="30" cy="80" r="2.5" fill="rgba(0,255,209,0.7)" />
        <circle cx="190" cy="80" r="2.5" fill="rgba(0,255,209,0.7)" />
      </svg>
    </div>
  );
});

EyeLogo.displayName = 'EyeLogo';
